import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ClipboardList, Search, MapPin, User, Calendar, FileSignature, ClipboardCheck, Receipt, Download, ChevronRight } from 'lucide-react';
import RoofAppraisalTab from '@/components/RoofAppraisalTab';

/**
 * Operations → Roof Report. A full-page version of the on-site inspection
 * form that lives as a tab on /appraisals/{id}. Same underlying data
 * (PATCH /api/appraisals/{id}/conditions) but richer surroundings:
 *   • Searchable appraisal picker with live filter
 *   • Hero card showing client + property + roof type + appraisal kind
 *   • Live progress indicator (X / 11 fields populated)
 *   • Embedded <RoofAppraisalTab/> reuses the form so we don't duplicate
 *     the field set or save logic between the two surfaces.
 */

const KIND_BADGE = {
  'Free Estimate':       { label: 'Free Estimate',        accent: '#D35400', bg: 'bg-orange-50',  text: 'text-[#A04000]',  border: 'border-[#D35400]/30', icon: ClipboardCheck },
  'Inspection Appraisal':{ label: 'Inspection Appraisal', accent: '#316536', bg: 'bg-emerald-50', text: 'text-[#234725]',  border: 'border-[#316536]/30', icon: FileSignature },
  'Appraisal Quote':     { label: 'Appraisal Quote',      accent: '#1E40AF', bg: 'bg-blue-50',    text: 'text-[#1E3A8A]',  border: 'border-[#1E40AF]/30', icon: Receipt },
};

// The 11 inspection-narrative fields the standalone page tracks progress for.
const TRACKED_FIELDS = [
  'roof_condition', 'roof_requirements', 'appraisal_condition',
  'ridge_condition', 'tile_condition', 'valley_condition', 'barge_condition',
  'spouting_condition', 'sheet_condition', 'fastening_condition', 'flashing_condition',
];

export default function RoofReportPage() {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const appraisalIdFromUrl = searchParams.get('appraisal') || '';
  const [appraisals, setAppraisals] = useState([]);
  const [appraisalId, setAppraisalId] = useState(appraisalIdFromUrl);
  const [filter, setFilter] = useState('');
  const [appraisal, setAppraisal] = useState(null);
  const [roofs, setRoofs] = useState([]);
  const [clientName, setClientName] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');

  // Load the picker list once. The CRM's appraisals endpoint already
  // denormalises client_name / property_address, which is exactly what
  // the picker needs to show — no extra queries required.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/appraisals?limit=400');
        setAppraisals(r.data?.items || []);
      } catch (e) { console.error(e); }
    })();
  }, [api]);

  // When an appraisal is selected, load the full doc + property roofs so
  // we can show the hero card AND pass them down to RoofAppraisalTab.
  const loadAppraisalContext = useCallback(async (id) => {
    if (!id) {
      setAppraisal(null); setRoofs([]); setClientName(''); setPropertyAddress('');
      return;
    }
    try {
      const a = await api.get(`/appraisals/${id}`);
      setAppraisal(a.data);
      // Property + roofs
      if (a.data.property_id) {
        const p = await api.get(`/properties/${a.data.property_id}`);
        setRoofs(p.data?.roof_details || []);
        const addr = [p.data?.property_address_num, p.data?.property_address_street].filter(Boolean).join(' ');
        const suburb = [addr, p.data?.property_address_suburb].filter(Boolean).join(', ');
        setPropertyAddress(suburb || '');
      }
      // Client
      if (a.data.client_id) {
        const c = await api.get(`/clients/${a.data.client_id}`);
        const nm = [c.data?.name_first, c.data?.name_last].filter(Boolean).join(' ') || c.data?.name_company || '';
        setClientName(nm);
      }
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { loadAppraisalContext(appraisalId); }, [appraisalId, loadAppraisalContext]);

  const onPickAppraisal = (id) => {
    setAppraisalId(id);
    setSearchParams(id ? { appraisal: id } : {});
  };

  // Live filter on the picker list — search across client name, address,
  // and appraisal access_id. Top-of-list keeps the most recent first
  // because the backend already sorts by appraisal_date desc.
  const filteredAppraisals = useMemo(() => {
    if (!filter.trim()) return appraisals;
    const needle = filter.toLowerCase();
    return appraisals.filter(a =>
      (a.client_name || '').toLowerCase().includes(needle) ||
      (a.property_address || '').toLowerCase().includes(needle) ||
      (a.access_id || '').toString().toLowerCase().includes(needle) ||
      (a._id || '').toLowerCase().includes(needle)
    );
  }, [appraisals, filter]);

  // Live progress — count how many of the 11 narrative fields are non-empty.
  const progress = useMemo(() => {
    if (!appraisal) return { filled: 0, total: TRACKED_FIELDS.length, pct: 0 };
    const filled = TRACKED_FIELDS.filter(f => (appraisal[f] || '').toString().trim().length > 0).length;
    return { filled, total: TRACKED_FIELDS.length, pct: Math.round((filled / TRACKED_FIELDS.length) * 100) };
  }, [appraisal]);

  const kindMeta = appraisal ? (KIND_BADGE[appraisal.quote_type_ref] || null) : null;
  const KindIcon = kindMeta?.icon;

  return (
    <div data-testid="roof-report-page" className="space-y-5">
      {/* Page Header */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#316536]" />
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#316536]/15 to-[#316536]/5 flex items-center justify-center ring-1 ring-[#316536]/10">
            <ClipboardList size={22} className="text-[#316536]" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Operations</p>
            <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Roof Report</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">Standalone, full-page on-site inspection workspace — same data as the Roof Appraisal tab on the appraisal detail page but with room to breathe. All edits save against the chosen appraisal and feed the Detailed Roof Appraisal PDF.</p>
          </div>
        </div>
      </div>

      {/* Appraisal Picker */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-2">
        <div className="flex items-center gap-3">
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 flex-shrink-0">Appraisal</label>
          <div className="relative flex-1 min-w-[280px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="rr-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by client, address, or appraisal #…"
              className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white focus:outline-none focus:border-[#316536]"
            />
          </div>
          <select
            data-testid="rr-appraisal-select"
            value={appraisalId}
            onChange={(e) => onPickAppraisal(e.target.value)}
            className="flex-1 min-w-[280px] max-w-md px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white focus:outline-none focus:border-[#316536]"
          >
            <option value="">— pick an appraisal —</option>
            {filteredAppraisals.slice(0, 200).map(a => (
              <option key={a._id} value={a._id}>
                A{a.appraisal_no || a.access_id || a._id.slice(-6)} · {a.client_name || 'Unknown'} · {a.property_address || ''}
              </option>
            ))}
          </select>
          {filter && (
            <span className="text-[11px] text-slate-400">{filteredAppraisals.length} match{filteredAppraisals.length === 1 ? '' : 'es'}</span>
          )}
        </div>
      </div>

      {!appraisalId || !appraisal ? (
        <div data-testid="rr-empty-state" className="bg-white border border-dashed border-[#E2E2DF] rounded-md p-16 text-center">
          <ClipboardList size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="text-sm text-slate-500 mb-1 font-semibold">No appraisal selected</p>
          <p className="text-xs text-slate-400">Pick an appraisal above to start building its Roof Report</p>
        </div>
      ) : (
        <>
          {/* Hero Card with client/property/kind + progress */}
          <div data-testid="rr-hero-card" className="bg-gradient-to-br from-slate-50 to-white border border-[#E2E2DF] rounded-md overflow-hidden">
            <div className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 bg-white border border-[#E2E2DF] rounded-full px-2.5 py-0.5 font-mono-num">A{appraisal.appraisal_no || appraisal.access_id || appraisal._id?.slice(-6)}</span>
                    {kindMeta && (
                      <span data-testid="rr-kind-badge" className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2.5 py-0.5 ${kindMeta.bg} ${kindMeta.text} border ${kindMeta.border}`}>
                        {KindIcon && <KindIcon size={11} />}
                        {kindMeta.label}
                      </span>
                    )}
                    {appraisal.appraisal_status && (
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">{appraisal.appraisal_status}</span>
                    )}
                  </div>
                  <h2 className="text-xl font-extrabold text-slate-900 font-[Manrope]" data-testid="rr-client-name">{clientName || 'Unnamed client'}</h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-500">
                    {propertyAddress && (
                      <span className="flex items-center gap-1.5" data-testid="rr-property-address"><MapPin size={13} className="text-slate-400" /> {propertyAddress}</span>
                    )}
                    {appraisal.appraisal_date && (
                      <span className="flex items-center gap-1.5 font-mono-num"><Calendar size={13} className="text-slate-400" /> {new Date(appraisal.appraisal_date).toLocaleDateString('en-NZ')}</span>
                    )}
                    {roofs[0]?.roof_type && (
                      <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#D35400]" /> Roof type: <span className="font-semibold text-slate-700">{roofs[0].roof_type}</span></span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link data-testid="rr-open-appraisal-link" to={`/appraisals/${appraisalId}`} className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#D35400] px-3 py-2 border border-[#E2E2DF] rounded-md hover:border-[#D35400] transition-all">
                    Open appraisal <ChevronRight size={12} />
                  </Link>
                  <button
                    data-testid="rr-download-pdf"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/reports/instant-roof-report/${appraisalId}`, { responseType: 'blob' });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement('a');
                        a.href = url; a.download = `Instant_Roof_Report_${appraisalId.slice(-6)}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(url);
                      } catch { alert('PDF generation failed'); }
                    }}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-[#316536] hover:bg-[#234725] text-white px-3 py-2 rounded-md transition-all"
                  >
                    <Download size={12} /> Generate PDF
                  </button>
                </div>
              </div>

              {/* Progress strip */}
              <div className="mt-5 pt-4 border-t border-slate-200/70">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Inspection progress</span>
                  <span data-testid="rr-progress-text" className="text-xs font-bold text-slate-700 font-mono-num">{progress.filled} / {progress.total} fields <span className="text-slate-400 font-normal">· {progress.pct}%</span></span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    data-testid="rr-progress-bar"
                    className="h-full bg-gradient-to-r from-[#D35400] to-[#316536] transition-all duration-500"
                    style={{ width: `${progress.pct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Inspection form body — reuses the tab component so we don't
              duplicate any field logic. `onSaved` re-fetches the appraisal
              so the progress strip updates in real time. */}
          <RoofAppraisalTab
            api={api}
            appraisalId={appraisalId}
            appraisal={appraisal}
            roofs={roofs}
            onSaved={() => loadAppraisalContext(appraisalId)}
            onJumpTab={null}
          />
        </>
      )}
    </div>
  );
}
